import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import client, { apiError } from "../api/client";
import { toast } from "sonner";
import SchoolAutocomplete from "../components/common/SchoolAutocomplete";
import SchoolEditModal from "../components/common/SchoolEditModal";
import { formatCurrency } from "../utils/format";
import { ArrowLeft, Plus, Trash2, Pencil, Loader2 } from "lucide-react";

const emptyForm = {
  date: "", time_from: "08:00", time_to: "12:00", school_id: "", program_ids: [],
  grade_id: "", lector_ids: [], seller_id: "", company_id: "",
  items: [{ program_name: "", count: 1, unit_price: 0 }],
  travel: { km: 0, price_per_km: 0, description: "" }, note: "", status_id: "",
};

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest font-medium text-muted-foreground mb-1.5">{label}</label>
      {children}
    </div>
  );
}
const inputCls = "w-full h-10 px-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20 focus:border-ring";

export default function WorkshopForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(emptyForm);
  const [school, setSchool] = useState(null);
  const [cat, setCat] = useState({ programs: [], grades: [], sellers: [], companies: [], statuses: [], lectors: [] });
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      client.get("/admin/programs"), client.get("/admin/grades"), client.get("/admin/sellers"),
      client.get("/admin/companies"), client.get("/admin/statuses"), client.get("/lectors"),
    ]).then(([p, g, s, c, st, l]) => {
      setCat({ programs: p.data, grades: g.data, sellers: s.data, companies: c.data, statuses: st.data, lectors: l.data });
    });
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    client.get(`/workshops/${id}`).then(async (res) => {
      const w = res.data;
      setForm({
        date: w.date, time_from: w.time_from, time_to: w.time_to, school_id: w.school_id,
        program_ids: w.program_ids || (w.program_id ? [w.program_id] : []), grade_id: w.grade_id || "", lector_ids: w.lector_ids || [],
        seller_id: w.seller_id || "", company_id: w.company_id || "",
        items: w.items?.length ? w.items : [{ program_name: "", count: 1, unit_price: 0 }],
        travel: w.travel || { km: 0, price_per_km: 0, description: "" }, note: w.note || "",
        status_id: w.status_id || "",
      });
      if (w.school_id) {
        const sc = await client.get(`/schools/${w.school_id}`);
        setSchool(sc.data);
      }
      setLoading(false);
    });
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) =>
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const setTravel = (k, v) => setForm((f) => ({ ...f, travel: { ...f.travel, [k]: v } }));
  const toggleLector = (lid) =>
    setForm((f) => ({
      ...f,
      lector_ids: f.lector_ids.includes(lid) ? f.lector_ids.filter((x) => x !== lid) : [...f.lector_ids, lid],
    }));

  const toggleProgram = (pid) =>
    setForm((f) => {
      const prog = cat.programs.find((p) => p.id === pid);
      const has = f.program_ids.includes(pid);
      let items = f.items;
      if (has) {
        const idx = items.findIndex((it) => it.program_name === (prog?.name || ""));
        if (idx >= 0) items = items.filter((_, i) => i !== idx);
      } else {
        const price = prog?.default_price || 0;
        const emptyIdx = items.findIndex((it) => !it.program_name);
        if (emptyIdx >= 0) {
          items = items.map((it, i) => (i === emptyIdx ? { ...it, program_name: prog?.name || "", unit_price: price || it.unit_price } : it));
        } else {
          items = [...items, { program_name: prog?.name || "", count: 1, unit_price: price }];
        }
      }
      return { ...f, program_ids: has ? f.program_ids.filter((x) => x !== pid) : [...f.program_ids, pid], items };
    });

  const itemsTotal = form.items.reduce((a, i) => a + (Number(i.count) || 0) * (Number(i.unit_price) || 0), 0);
  const travelTotal = (Number(form.travel.km) || 0) * (Number(form.travel.price_per_km) || 0);
  const revenue = itemsTotal + travelTotal;

  const save = async () => {
    if (!form.date) return toast.error("Vyplňte datum");
    if (!form.school_id) return toast.error("Vyberte školu");
    setSaving(true);
    const payload = {
      ...form,
      items: form.items.map((i) => ({ program_name: i.program_name, count: Number(i.count), unit_price: Number(i.unit_price) })),
      travel: { km: Number(form.travel.km), price_per_km: Number(form.travel.price_per_km), description: form.travel.description },
    };
    try {
      if (isEdit) await client.put(`/workshops/${id}`, payload);
      else await client.post("/workshops", payload);
      toast.success(isEdit ? "Workshop uložen" : "Workshop vytvořen");
      navigate("/workshops");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div className="p-8 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Načítání…</div>;

  const sel = (val, onChange, opts, placeholder) => (
    <select value={val} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      <option value="">{placeholder}</option>
      {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  );

  return (
    <>
      <div className="h-16 border-b border-border px-8 flex items-center gap-3 sticky top-0 bg-background/80 backdrop-blur-md z-20">
        <button onClick={() => navigate("/workshops")} className="p-2 rounded-md hover:bg-secondary" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="font-heading text-xl font-semibold tracking-tight">{isEdit ? "Upravit workshop" : "Nový workshop"}</h1>
      </div>

      <div className="p-8 max-w-3xl space-y-6" data-testid="workshop-form">
        <div className="grid grid-cols-3 gap-4">
          <Field label="Datum"><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="ws-date" /></Field>
          <Field label="Čas od"><input type="time" value={form.time_from} onChange={(e) => set("time_from", e.target.value)} className={inputCls} /></Field>
          <Field label="Čas do"><input type="time" value={form.time_to} onChange={(e) => set("time_to", e.target.value)} className={inputCls} /></Field>
        </div>

        <Field label="Škola">
          <div className="flex gap-2">
            <div className="flex-1"><SchoolAutocomplete value={school} onSelect={(s) => { setSchool(s); set("school_id", s.id); }} /></div>
            {school && (
              <button type="button" onClick={() => setShowSchoolModal(true)} data-testid="edit-school-btn" className="h-10 px-3 rounded-md border border-border text-sm hover:bg-secondary flex items-center gap-1">
                <Pencil className="h-4 w-4" /> Upravit
              </button>
            )}
            <button type="button" onClick={() => { setSchool(null); setShowSchoolModal(true); }} data-testid="new-school-btn" className="h-10 px-3 rounded-md border border-border text-sm hover:bg-secondary flex items-center gap-1">
              <Plus className="h-4 w-4" /> Nová
            </button>
          </div>
          {school && <div className="text-xs text-muted-foreground mt-1.5">{[school.street, school.zip, school.city].filter(Boolean).join(", ")}</div>}
        </Field>

        <Field label="Programy (lze vybrat více)">
          <div className="flex flex-wrap gap-2" data-testid="program-picker">
            {cat.programs.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`program-toggle-${p.id}`}
                onClick={() => toggleProgram(p.id)}
                className={`px-3 h-9 rounded-full border text-sm transition-colors ${form.program_ids.includes(p.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
              >
                {p.name}{p.default_price ? ` · ${p.default_price} Kč` : ""}
              </button>
            ))}
            {cat.programs.length === 0 && <span className="text-sm text-muted-foreground">Žádné programy</span>}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Stupeň školy">{sel(form.grade_id, (v) => set("grade_id", v), cat.grades, "Vyberte stupeň")}</Field>
          <Field label="Prodejce">{sel(form.seller_id, (v) => set("seller_id", v), cat.sellers, "Vyberte prodejce")}</Field>
          <Field label="Fakturační společnost">{sel(form.company_id, (v) => set("company_id", v), cat.companies, "Vyberte společnost")}</Field>
          <Field label="Stav">{sel(form.status_id, (v) => set("status_id", v), cat.statuses, "Vyberte stav")}</Field>
        </div>

        <Field label="Lektoři">
          <div className="flex flex-wrap gap-2" data-testid="lector-picker">
            {cat.lectors.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => toggleLector(l.id)}
                data-testid={`lector-toggle-${l.id}`}
                className={`px-3 h-9 rounded-full border text-sm transition-colors ${form.lector_ids.includes(l.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
              >
                {l.name}
              </button>
            ))}
            {cat.lectors.length === 0 && <span className="text-sm text-muted-foreground">Žádní lektoři</span>}
          </div>
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Položky workshopu</label>
            <button type="button" onClick={() => set("items", [...form.items, { program_name: "", count: 1, unit_price: 0 }])} data-testid="add-item-btn" className="text-sm flex items-center gap-1 hover:text-foreground text-muted-foreground">
              <Plus className="h-4 w-4" /> Přidat položku
            </button>
          </div>
          <div className="space-y-2">
            {form.items.map((it, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input placeholder="Název položky / program" value={it.program_name} onChange={(e) => setItem(i, "program_name", e.target.value)} className="flex-1 min-w-0 h-10 px-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20" data-testid={`item-name-${i}`} />
                <input type="number" placeholder="ks" value={it.count} onChange={(e) => setItem(i, "count", e.target.value)} className="w-20 h-10 px-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20" data-testid={`item-count-${i}`} />
                <input type="number" placeholder="Kč/ks" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="w-28 h-10 px-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20" data-testid={`item-price-${i}`} />
                <button type="button" onClick={() => set("items", form.items.filter((_, idx) => idx !== i))} className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Cestovní náklady</label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <input type="number" placeholder="km" value={form.travel.km} onChange={(e) => setTravel("km", e.target.value)} className={inputCls} data-testid="travel-km" />
            <input type="number" placeholder="Kč / km" value={form.travel.price_per_km} onChange={(e) => setTravel("price_per_km", e.target.value)} className={inputCls} data-testid="travel-price" />
            <input placeholder="Popis" value={form.travel.description} onChange={(e) => setTravel("description", e.target.value)} className={inputCls} />
          </div>
        </div>

        <Field label="Poznámka">
          <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={3} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20" data-testid="ws-note" />
        </Field>

        <div className="flex items-center justify-between border-t border-border pt-5">
          <div className="text-sm space-y-0.5">
            <div><span className="text-muted-foreground">Položky: </span><span data-testid="ws-items-total">{formatCurrency(itemsTotal)}</span></div>
            <div><span className="text-muted-foreground">Cestovné: </span><span data-testid="ws-travel-total">{formatCurrency(travelTotal)}</span></div>
            <div className="pt-1"><span className="text-muted-foreground">Celkem: </span><span className="font-heading text-xl font-semibold" data-testid="ws-total">{formatCurrency(revenue)}</span></div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate("/workshops")} className="h-10 px-4 rounded-md border border-border text-sm hover:bg-secondary">Zrušit</button>
            <button onClick={save} disabled={saving} data-testid="ws-save-btn" className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Uložit workshop
            </button>
          </div>
        </div>
      </div>

      {showSchoolModal && (
        <SchoolEditModal
          school={school}
          onClose={() => setShowSchoolModal(false)}
          onSaved={(s) => { setSchool(s); set("school_id", s.id); setShowSchoolModal(false); }}
        />
      )}
    </>
  );
}
